import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { TCLabel } from "components/TopCourtComponents/Label/TopCourtLabel";
import ZoomToolbar from "./zoomToolbar";
import { Avatar } from "@mui/material";
// import SlideDialog from "components/SlideDialog/SlideDialog";
// import MatchGameForm from "pages/Tournament/Match/Component/common/MatchGameForm";
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { WinnerCheckSolid } from "components/TopCourtComponents/Image";
import { color } from "constant/constant";
import { generateCircleAvatar } from "helper/User";
import { loadImages } from "helper/Image";

function generateSequentialSingleElim({ seeds, size, hasThirdPlace = false, nextRoundSeeds }) {
  const ordered = [...seeds].sort((a, b) => a.number - b.number);
  const totalRounds = Math.log2(size);
  const rounds = [];
  const r1 = [];
  for (let i = 0; i < size / 2; i++) {
    const a = ordered[2 * i]?.number ?? 2 * i + 1;
    const b = ordered[2 * i + 1]?.number ?? 2 * i + 2;
    r1.push({
      id: ordered[2 * i]?.matchId || `R1-M${i + 1}`,
      round: 1,
      index: i + 1,
      sides: {
        home: { source: { type: "seed", seedNumber: a } },
        away: { source: { type: "seed", seedNumber: b } },
      },
    });
  }
  rounds.push({ round: 1, matches: r1 });

  for (let r = 2; r <= totalRounds; r++) {
    const prev = rounds[r - 2].matches;
    const nextRound = [...nextRoundSeeds].filter(v => v.round == r).sort((a, b) => a.number - b.number)
    const count = prev.length / 2;
    const matches = Array.from({ length: count }, (_, i) => {
      const id = `R${r}-M${i + 1}`;
      const leftPrev = prev[2 * i];
      const rightPrev = prev[2 * i + 1];
      const a = nextRound[2 * i]?.number ? (nextRound[2 * i]?.number == 2 * i + 1) ? nextRound[2 * i]?.number : 2 * i + 1 : 2 * i + 1;
      const b = nextRound[2 * i + 1]?.number ? (nextRound[2 * i + 1]?.number == 2 * i + 2) ? nextRound[2 * i + 1]?.number : 2 * i + 2 : 2 * i + 2;
      return {
        id,
        round: r,
        index: i + 1,
        sides: {
          home: { source: { type: "winner", round: r, seedNumber: a, fromMatchId: leftPrev.id } },
          away: { source: { type: "winner", round: r, seedNumber: b, fromMatchId: rightPrev.id } },
        },
      };
    });

    prev.forEach((m, idx) => {
      const toIdx = Math.floor(idx / 2);
      const as = idx % 2 === 0 ? "home" : "away";
      if (matches[toIdx]) m.next = { winnerTo: matches[toIdx].id, as };
    });

    rounds.push({ round: r, matches });
  }

  let thirdPlaceMatchId;
  if (hasThirdPlace && totalRounds >= 2) {
    const semiRound = rounds[totalRounds - 2];
    const lastRound = rounds[rounds.length - 1];
    thirdPlaceMatchId = `R${totalRounds}-T3`;
    lastRound.matches.push({
      id: thirdPlaceMatchId,
      round: totalRounds,
      index: lastRound.matches.length + 1,
      sides: {
        home: { source: { type: "loser", fromMatchId: semiRound.matches[0].id } },
        away: { source: { type: "loser", fromMatchId: semiRound.matches[1].id } },
      },
    });
  }

  return { rounds, thirdPlaceMatchId };
}

function computeLayout(rounds, cfg, type = "singles") {
  const colW = cfg?.colW ?? 260; // column width
  const boxW = cfg?.boxW ?? 220;
  const boxH = type === "doubles" ? 110 : (cfg?.boxH ?? 70);
  const vGap = cfg?.vGap ?? 24; // vertical gap between R1 matches

  const r1 = rounds[0];
  const r1Items = r1.matches.map((m, i) => ({
    match: m,
    box: { x: 0, y: i * (boxH + vGap) + boxH / 2, w: boxW, h: boxH },
  }));

  const columns = [{ round: 1, items: r1Items }];

  for (let r = 2; r <= rounds.length; r++) {
    const prev = columns[r - 2].items;
    const curMatches = rounds[r - 1].matches;
    const items = curMatches.map((m) => {
      const srcHome = prev.find((it) => it.match.id === m.sides.home.source.fromMatchId);
      const srcAway = prev.find((it) => it.match.id === m.sides.away.source.fromMatchId);
      const y = srcHome && srcAway ? (srcHome.box.y + srcAway.box.y) / 2 : 0;
      return { match: m, box: { x: (r - 1) * colW, y, w: boxW, h: boxH } };
    });
    columns.push({ round: r, items });
  }

  const width = columns.length * colW + 40; // padding
  const lastCol = columns[0].items; // R1 determines height grid
  const height = (lastCol.length - 1) * (boxH + vGap) + boxH + 40; // padding

  return { columns, width, height, metrics: { colW, boxW, boxH, vGap } };
}

function computeLayoutRTL(rounds, cfg, type = "singles") {
  const base = computeLayout(rounds, cfg, type);
  const colW = cfg?.colW ?? 260;
  const width = base.columns.length * colW + 40;
  const columns = base.columns.map((col) => {
    const flippedItems = col.items.map((it) => ({
      match: it.match,
      box: { ...it.box, x: width - it.box.x - it.box.w - 24 }, // 24px gutter to the outer edge
    }));
    return { round: col.round, items: flippedItems };
  });
  return { columns, width, height: base.height, metrics: base.metrics };
}

// ---- SideLabel (with champion auto-scroll hook) ----
function SideLabel({ source, seeds, type, nextRoundSeeds = [], reloadBracket, onChampionMount }) {
  let s = null;
  const sideLabel = (n) => (n % 2 ? 'a' : 'b');
  const [openDialog, setOpenDialog] = useState(false);
  const championRef = useRef(null);

  if (source.type === "seed") {
    s = seeds.find((x) => x.number === source.seedNumber);
  }
  if (source.type === "winner") {
    s = [...nextRoundSeeds].filter(v => v.round === source.round).find(v => v.number === source.seedNumber);
  }
  if (source.type === "final") {
    console.log('here type final', source.round)
    s = [...nextRoundSeeds].filter(v => v.round === source.round).find(v => v.number === source.seedNumber);
  }

  useEffect(() => {
    if (s?.isFinalChampion && championRef.current) {
      onChampionMount?.(championRef.current);
    }
  }, [s?.isFinalChampion, onChampionMount]);

  if (s) {
    const winnerSide = s.match?.winner !== "draw"
      ? (s.match?.isTournament ? (s.match?.endMatch ? s.match?.winner : '') : s.match?.winner)
      : null;
    const side = source.seedNumber % 2 === 0 ? "b" : "a";

    return (
      <>
        {s.participants?.userIds && (
          <div
            ref={s.isFinalChampion ? championRef : null}
            style={{ display: "flex", width: '100%', justifyContent: 'space-between' }}
            onClick={() => { if (s.match) { setOpenDialog(true); } }}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', opacity: winnerSide && (winnerSide === side ? 1 : 0.4), gap: 4 }}>
                {s.participants?.userIds.map(v => (
                  <Avatar
                    key={v._id || v.userId}
                    sx={{ width: 20, height: 20, zIndex: 0 }}
                    src={loadImages(v.verifyId?.idPhoto, true, 250)}
                    imgProps={{ loading: 'lazy' }}
                  />
                ))}
              </div>
              <div style={{ opacity: winnerSide && (winnerSide === side ? 1 : 0.4) }}>
                {s.participants?.userIds.map(v => (
                  <TCLabel key={`name-${v._id || v.userId}`}>{v.verifyId?.idName}</TCLabel>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              {((winnerSide && winnerSide == side) && !s.isFinalChampion) &&
                <WinnerCheckSolid color={color.blueIndoor_2} />
              }
              {s.isFinalChampion && (
                <EmojiEventsIcon sx={{ width: '1em', height: '1em', color: '#fad142' }} />
              )}
              {
                (s.match?.isWO && winnerSide != side) &&
                <TCLabel subtitle style={{
                  fontStyle: "italic"
                }}>Walkover</TCLabel>
              }
              {s.match?.matchScores
                ?.filter(ss => ss.side === side)
                .map(a => {
                  const opposite = side === "a" ? "b" : "a";
                  let woWinner = false
                  if (s.match?.isWO) {
                      woWinner = (winnerSide == side)
                  }
                  const b = s.match?.matchScores.find(x => x.side === opposite && x.roundNumber === a.roundNumber);
                  const highlight = b ? a.score > b.score || a.score == b.score : false;
                  return (
                    <TCLabel key={`${a.roundNumber}-${side}`} style={{ opacity: s.match?.isWO ? woWinner ? 1 : 0.4 : s.match?.endMatch ? highlight ? 1 : 0.4 : 1}}>
                      {a.score}
                    </TCLabel>
                  );
                })}
            </div>
          </div>
        )}

        {!s.participants?.userIds && (
          <div style={{ display: 'flex', gap: 8 }}>
            <div>
              <Avatar
                sx={{ width: 20, height: 20, zIndex: 0 }}
                src={generateCircleAvatar(s.description || 'W')}
                imgProps={{ loading: 'lazy' }}
              />
              {type === "doubles" && (
                <Avatar
                  sx={{ width: 20, height: 20, zIndex: 0, marginTop: '2px' }}
                  src={generateCircleAvatar(s.description || 'W')}
                  imgProps={{ loading: 'lazy' }}
                />
              )}
            </div>
            <div>
              <TCLabel>{s?.description || ""}</TCLabel>
              {type === "doubles" && (
                <TCLabel style={{ marginTop: 2 }}>{s?.description || ""}</TCLabel>
              )}
            </div>
          </div>
        )}

        {/* {(openDialog && s.participants?.userIds) && (
          <SlideDialog
            isOpen={openDialog}
            setIsOpen={() => setOpenDialog(false)}
          >
            <MatchGameForm
              match={s.match}
              isOpen={openDialog}
              setOpenDialog={() => setOpenDialog(false)}
              loadMatchDetail={() => {
                reloadBracket();
                setOpenDialog(false);
              }}
              loadInfoOnly={() => {
                reloadBracket();
              }}
              setLoading={() => {}}
            />
          </SlideDialog>
        )} */}
      </>
    );
  }

  return (
    <div>
      <TCLabel>TBD</TCLabel>
      {type === "doubles" && <TCLabel>TBD</TCLabel>}
    </div>
  );
}

function Connectors({ layout }) {
  const stroke = "#025597";
  const strokeWidth = 1;
  const paths = [];

  for (let c = 1; c < layout.columns.length; c++) {
    const prevCol = layout.columns[c - 1].items;
    const col = layout.columns[c].items;
    for (const m of col) {
      const srcHome = prevCol.find((it) => it.match.id === m.match.sides.home.source.fromMatchId);
      const srcAway = prevCol.find((it) => it.match.id === m.match.sides.away.source.fromMatchId);
      if (!srcHome || !srcAway) continue;

      const x1 = srcHome.box.x + srcHome.box.w + 12;
      const y1 = srcHome.box.y;
      const x2 = m.box.x + 12;
      const y2 = m.box.y;

      const x3 = srcAway.box.x + srcAway.box.w + 12;
      const y3 = srcAway.box.y;
      const y4 = m.box.y;

      const mid = (x1 + x2) / 2;
      const mid2 = (x3 + x2) / 2;

      paths.push(
        <path key={`${m.match.id}-home`} d={`M ${x1} ${y1} H ${mid} V ${y2} H ${x2}`} fill="none" stroke={stroke} strokeWidth={strokeWidth} />
      );
      paths.push(
        <path key={`${m.match.id}-away`} d={`M ${x3} ${y3} H ${mid2} V ${y4} H ${x2}`} fill="none" stroke={stroke} strokeWidth={strokeWidth} />
      );
    }
  }

  return <>{paths}</>;
}

function ConnectorsRTL({ layout }) {
  const stroke = "#025597";
  const strokeWidth = 1;
  const paths = [];

  for (let c = 1; c < layout.columns.length; c++) {
    const prevCol = layout.columns[c - 1].items;
    const col = layout.columns[c].items;
    for (const m of col) {
      const srcHome = prevCol.find((it) => it.match.id === m.match.sides.home.source.fromMatchId);
      const srcAway = prevCol.find((it) => it.match.id === m.match.sides.away.source.fromMatchId);
      if (!srcHome || !srcAway) continue;

      const x1 = srcHome.box.x - 12;
      const y1 = srcHome.box.y;
      const x2 = m.box.x + m.box.w - 15;
      const y2 = m.box.y;

      const x3 = srcAway.box.x - 12;
      const y3 = srcAway.box.y;
      const y4 = m.box.y;

      const mid = (x1 + x2) / 2;
      const mid2 = (x3 + x2) / 2;

      paths.push(
        <path key={`${m.match.id}-home-rtl`} d={`M ${x1} ${y1} H ${mid} V ${y2} H ${x2}`} fill="none" stroke={stroke} strokeWidth={strokeWidth} />
      );
      paths.push(
        <path key={`${m.match.id}-away-rtl`} d={`M ${x3} ${y3} H ${mid2} V ${y4} H ${x2}`} fill="none" stroke={stroke} strokeWidth={strokeWidth} />
      );
    }
  }

  return <>{paths}</>;
}

// ---- Main component ----
export default function BracketTreeTwoSided({ seeds, type, hasThirdPlace = false, reloadBracket = () => {} }) {
  const size = seeds.filter((s) => (!s.round || s.round == 1)).length;
  const isTwoSided = size > 16;

  const leftSeeds = isTwoSided
    ? seeds.filter((s) => (!s.round || s.round == 1)).filter((s) => s.number <= size / 2)
    : seeds.filter((s) => (!s.round || s.round == 1));
  const rightSeeds = isTwoSided
    ? seeds.filter((s) => (!s.round || s.round == 1)).filter((s) => s.number > size / 2)
    : [];

  const nextPow2 = (n) => {
    if (n <= 1) return 1;
    n = n - 1;
    n |= n >> 1;
    n |= n >> 2;
    n |= n >> 4;
    n |= n >> 8;
    n |= n >> 16;
    return n + 1;
  };

  const nextRoundLeftSeeds = isTwoSided
    ? seeds.filter((s) => (s.round && s.round > 1)).filter((s) => s.number <= Math.floor((size / 2) / s.round))
    : seeds.filter((s) => (s.round && s.round > 1));

  const nextRoundRightSeeds = isTwoSided
    ? seeds
        .filter((s) => (s.round && s.round > 1))
        .filter((s) => s.number > Math.floor(size / nextPow2(size / 2)))
        .map(v => {
          let round = nextPow2(v.round);
          let newSize = Math.floor(size / round) / 2;
          let newNumber = v.number - newSize
          if (round == 4 && v.round == 4 && newSize == 4) { newNumber = Math.floor(v.number / 2) }
          return { ...v, number: newNumber };
        })
    : [];

  const leftBracket = useMemo(
    () => generateSequentialSingleElim({ seeds: leftSeeds, size: leftSeeds.length, hasThirdPlace: false, nextRoundSeeds: nextRoundLeftSeeds }),
    [leftSeeds, nextRoundLeftSeeds]
  );
  const rightBracket = useMemo(
    () => (isTwoSided ? generateSequentialSingleElim({ seeds: rightSeeds, size: rightSeeds.length, hasThirdPlace: false, nextRoundSeeds: nextRoundRightSeeds }) : undefined),
    [isTwoSided, rightSeeds, nextRoundRightSeeds]
  );

  console.log('here rightBracket.rounds.length ', rightBracket?.rounds.length)
  const center = useMemo(() => {
    if (!isTwoSided || !rightBracket) return undefined;
    const leftFinal = nextRoundLeftSeeds.find(v => v.round == rightBracket.rounds.length + 1 && v.number == 1);
    const rightFinal = nextRoundLeftSeeds.find(v => v.round == rightBracket.rounds.length + 1 && v.number == 2);
    console.log(rightFinal, leftFinal)
    const final = {
      id: "FINAL",
      round: 1,
      index: 1,
      sides: {
        home: { source: { type: "final", round: rightBracket.rounds.length + 1, fromMatchId: leftFinal?._id, seedNumber: 1 } },
        away: { source: { type: "final", round: rightBracket.rounds.length + 1, fromMatchId: rightFinal?._id, seedNumber: 2 } },
      },
    };
    const rounds = [{ round: 1, matches: [final] }];
    if (hasThirdPlace) {
      rounds[0].matches.push({
        id: "T3",
        round: 1,
        index: 2,
        sides: {
          home: { source: { type: "loser", fromMatchId: leftFinal.id } },
          away: { source: { type: "loser", fromMatchId: rightFinal.id } },
        },
      });
    }
    return { rounds };
  }, [isTwoSided, hasThirdPlace, leftBracket.rounds, rightBracket?.rounds]);

  const leftLayout = useMemo(() => computeLayout(leftBracket.rounds, null, type), [leftBracket.rounds, type]);
  const rightLayout = useMemo(() => (rightBracket ? computeLayoutRTL(rightBracket.rounds, null, type) : undefined), [rightBracket?.rounds, type]);
  const centerLayout = useMemo(() => (center ? computeLayout(center.rounds, { colW: 280, boxW: 260, boxH: 70 }, type) : undefined), [center?.rounds, type]);

  const spacing = 50;
  const totalWidth = isTwoSided
    ? leftLayout.width + (centerLayout ? centerLayout.width : 0) + (rightLayout ? rightLayout.width : 0) - spacing * 2.5
    : leftLayout.width;
  const totalHeight = Math.max(leftLayout.height, rightLayout ? rightLayout.height : 0, centerLayout ? centerLayout.height : 0);

  let centerTopOffset = 0;
  if (isTwoSided && centerLayout && leftLayout && rightLayout) {
    const leftFinalY = leftLayout.columns[leftLayout.columns.length - 1].items[0]?.box.y ?? 0;
    const rightFinalY = rightLayout.columns[rightLayout.columns.length - 1].items[0]?.box.y ?? leftFinalY;
    const desiredCenterY = (leftFinalY + rightFinalY) / 2;
    const currentCenterY = centerLayout.columns[0].items[0]?.box.y ?? 0;
    centerTopOffset = desiredCenterY - currentCenterY;
  }

  // ---- Zoom & Pan ----
  const [zoom, setZoom] = useState(1);
  const minZoom = 0.4;
  const maxZoom = 2.0;
  const step = 0.1;
  const boardRef = useRef(null);
  const setZoomClamped = (z) => setZoom(Math.min(maxZoom, Math.max(minZoom, Number(z.toFixed(2)))));
  const zoomIn = () => setZoomClamped(zoom + step);
  const zoomOut = () => setZoomClamped(zoom - step);
  const reset = () => setZoom(1);

  const onWheel = useCallback(
    (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const dir = e.deltaY > 0 ? -1 : 1;
      setZoomClamped(zoom + dir * step);
    },
    [zoom]
  );

  const isDragging = useRef(false);
  const dragStart = useRef(null);
  const onMouseDown = (e) => {
    if (!boardRef.current) return;
    isDragging.current = true;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      left: boardRef.current.scrollLeft,
      top: boardRef.current.scrollTop,
    };
    e.target.style.cursor = "grabbing";
  };
  const onMouseMove = (e) => {
    if (!boardRef.current || !isDragging.current || !dragStart.current) return;
    boardRef.current.scrollLeft = dragStart.current.left - (e.clientX - dragStart.current.x);
    boardRef.current.scrollTop = dragStart.current.top - (e.clientY - dragStart.current.y);
  };
  const onMouseUp = (e) => {
    isDragging.current = false;
    e.target.style.cursor = "";
  };

  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;
    const stop = (e) => { if (e.ctrlKey) e.preventDefault(); };
    el.addEventListener("wheel", stop, { passive: false });
    return () => el.removeEventListener("wheel", stop);
  }, []);

  // ---- Champion auto-scroll ----
  const hasScrolledToChampion = useRef(false);

  // If you reload brackets and want to allow re-scroll, uncomment:
  // useEffect(() => { hasScrolledToChampion.current = false; }, [seeds]);

  const scrollToChampion = useCallback((el) => {
    if (!boardRef.current || !el || hasScrolledToChampion.current) return;

    const container = boardRef.current;
    const elRect = el.getBoundingClientRect();
    const contRect = container.getBoundingClientRect();

    const currentLeft = container.scrollLeft;
    const currentTop  = container.scrollTop;

    const targetLeft = currentLeft + (elRect.left - contRect.left);
    const targetTop  = currentTop  + (elRect.top  - contRect.top);

    const centerLeft = Math.max(0, targetLeft - (container.clientWidth  - elRect.width)  / 2);
    const centerTop  = Math.max(0, targetTop  - (container.clientHeight - elRect.height) / 2);

    container.scrollTo({ left: centerLeft, top: centerTop, behavior: "smooth" });
    hasScrolledToChampion.current = true;
  }, []);

  return (
    <div className="btw-wrap">
      <ZoomToolbar
        zoom={zoom}
        setZoom={(z) => setZoom(Math.min(2, Math.max(0.4, Number(z.toFixed(2)))))}
        onZoomIn={() => setZoom((z) => Math.min(2, Number((z + 0.1).toFixed(2))))}
        onZoomOut={() => setZoom((z) => Math.max(0.4, Number((z - 0.1).toFixed(2))))}
        onReset={() => setZoom(1)}
      />

      <div
        ref={boardRef}
        className="btw-board"
        style={{
          position: "relative",
          overflow: "auto",
          borderRadius: 12,
          padding: 0,
          height: totalHeight + 40,
          cursor: "grab",
          background: "transparent",
        }}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseUp}
        onMouseUp={onMouseUp}
      >
        <div style={{ position: "relative", width: totalWidth * zoom, height: totalHeight * zoom }}>
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: totalWidth,
              height: totalHeight,
              transform: `scale(${zoom})`,
              transformOrigin: "0 0",
            }}
          >
            {/* Connectors */}
            <svg width={totalWidth} height={totalHeight} style={{ display: "block" }}>
              <g transform={`translate(0,0)`}>
                <Connectors layout={leftLayout} />
              </g>
              {isTwoSided && rightLayout && (
                <g transform={`translate(${leftLayout.width - (50 + 60) + (centerLayout ? centerLayout.width : 0)},0)`}>
                  <ConnectorsRTL layout={rightLayout} />
                </g>
              )}
            </svg>

            {/* Cards */}
            <div style={{ position: "absolute", left: 0, top: 0, width: totalWidth, height: totalHeight }}>
              {/* left cards */}
              {leftLayout.columns.map((col) =>
                col.items.map((ml) => (
                  <div
                    key={ml.match.id}
                    className="btw-card"
                    style={{ left: ml.box.x + 12, top: ml.box.y - ml.box.h / 2, width: ml.box.w, height: ml.box.h }}
                  >
                    <div className="btw-side">
                      <SideLabel
                        source={ml.match.sides.home.source}
                        seeds={leftSeeds}
                        nextRoundSeeds={nextRoundLeftSeeds}
                        type={type}
                        reloadBracket={reloadBracket}
                        onChampionMount={scrollToChampion}
                      />
                    </div>
                    <div style={{ height: 0.5, backgroundColor: color.blueIndoor_1, margin: "2px 0px" }} />
                    <div className="btw-side" style={{ marginBottom: 2 }}>
                      <SideLabel
                        source={ml.match.sides.away.source}
                        seeds={leftSeeds}
                        nextRoundSeeds={nextRoundLeftSeeds}
                        type={type}
                        reloadBracket={reloadBracket}
                        onChampionMount={scrollToChampion}
                      />
                    </div>
                  </div>
                ))
              )}

              {/* center cards */}
              {isTwoSided && center && centerLayout && (
                <div
                  style={{
                    position: "absolute",
                    left: leftLayout.width - 50,
                    top: centerTopOffset,
                    width: centerLayout.width,
                    height: centerLayout.height,
                  }}
                >
                  {centerLayout.columns[0].items.map((ml) => (
                    <div
                      key={ml.match.id}
                      className="btw-card"
                      style={{ left: ml.box.x + 12, top: ml.box.y - ml.box.h / 2, width: ml.box.w, height: ml.box.h }}
                    >
                      <div className="btw-side">
                        <SideLabel
                          source={ml.match.sides.home.source}
                          nextRoundSeeds={nextRoundLeftSeeds}
                          type={type}
                          reloadBracket={reloadBracket}
                          onChampionMount={scrollToChampion}
                        />
                      </div>
                      <div style={{ height: 0.5, backgroundColor: color.blueIndoor_1, margin: "2px 0px" }} />
                      <div className="btw-side">
                        <SideLabel
                          source={ml.match.sides.away.source}
                          nextRoundSeeds={nextRoundLeftSeeds}
                          type={type}
                          reloadBracket={reloadBracket}
                          onChampionMount={scrollToChampion}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* right cards */}
              {isTwoSided && rightLayout && (
                <div
                  style={{
                    position: "absolute",
                    left: leftLayout.width + (centerLayout ? centerLayout.width : 0) - (50 + 86),
                    top: 0,
                    width: rightLayout.width,
                    height: rightLayout.height,
                  }}
                >
                  {rightLayout.columns.map((col) =>
                    col.items.map((ml) => (
                      <div
                        key={ml.match.id}
                        className="btw-card"
                        style={{ left: ml.box.x + 12, top: ml.box.y - ml.box.h / 2, width: ml.box.w, height: ml.box.h }}
                      >
                        <div className="btw-side">
                          <SideLabel
                            source={ml.match.sides.home.source}
                            seeds={rightSeeds}
                            nextRoundSeeds={nextRoundRightSeeds}
                            type={type}
                            reloadBracket={reloadBracket}
                            onChampionMount={scrollToChampion}
                          />
                        </div>
                        <div style={{ height: 0.5, backgroundColor: color.blueIndoor_1, margin: "2px 0px" }} />
                        <div className="btw-side">
                          <SideLabel
                            source={ml.match.sides.away.source}
                            seeds={rightSeeds}
                            nextRoundSeeds={nextRoundRightSeeds}
                            type={type}
                            reloadBracket={reloadBracket}
                            onChampionMount={scrollToChampion}
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}